import PatientModel from '@/models/patient-model';
import DoctorModel from '@/models/doctor-model';
import ClientModel from '@/models/client-model';
import { dateFormat, getServerISODate } from '@/helpers/dateFormat';
import IPrescription, { IPrescrPermissaoAlterDTO, EnteralPrescricaoDTO } from '@/interfaces/prescription';
import PrescriptionBaseModel from '@/models/prescription-base-model';
import { ADMINISTRATION_WAYS_CENTRAL } from '@/settings/consts/prescription-params';
import * as prescriptionTypes from '@/settings/consts/prescriptions-types';
import * as transcriptionTypes from '@/settings/consts/transcriptions-types';
import { toNumber } from '@/helpers/toNumber';
import { toUpperCase } from '@/helpers/toUpperCase';
import { IdNomeDTO } from '@/interfaces/base-dto';

export default class PrescriptionModel extends PrescriptionBaseModel {
  public idPrescricao?: string;
  public idPrescricaoCopia: string;
  public dataPrescricaoCopia: string;
  public idTemplate: string;
  public nomeArquivoTranscricao: string;
  public pathArquivoTranscricao: string;
  public template: any;
  public idSiPro?: string;
  public seqsSiProMultiPrescricao?:string[];
  public idTranscricao: string;
  public transcricao: any;
  public unidade: any;
  public medico: DoctorModel;
  public dataPrescricao: any[];
  public dataPrescricaoTime: string;
  public dataProducao: Date;
  public dataCriacao: string;
  public dataCriacaoTime: string;
  public endereco: any;
  public registro: any;
  public convenio: any;
  public leito: any;
  public setor: any;
  public same: any;
  public tipoPrescricao: string;
  public descricaoTipo: string;
  public tipoTranscricao: string;
  public terapia: IdNomeDTO;
  public tnDays: string;
  public copy: boolean;
  public model: boolean;
  public profile: boolean;
  public pattern: boolean;
  public transcricaoEmProgresso: boolean;
  public transcriptionCopy: boolean;
  public responsavel: any;
  public permissao: IPrescrPermissaoAlterDTO;
  public enteral: EnteralPrescricaoDTO;
  public observacao: string;
  public observacaoPrescricao?: string;
  public codInsumosCienteObrig:Array<string>;
  public validar: boolean;
  public msgPodeValidar: string;

  constructor(_initialValue?: IPrescription) {
    super(_initialValue);

    const initialValue = _initialValue || {
      idPrescricao: null,
      idPrescricaoCopia: null,
      dataPrescricaoCopia: null,
      idTemplate: null,
      template: null,
      idTranscricao: null,
      nomeArquivoTranscricao: null,
      pathArquivoTranscricao: null,
      idSiPro: null,
      seqsSiProMultiPrescricao: null,
      unidade: {},
      cliente: new ClientModel(),
      medico: new DoctorModel(),
      paciente: new PatientModel(),
      volumeFinalMlKgDia: null,
      volumeDigitadoPorKg: false,
      divisaoBolsas: 1,
      bolsaMulticompart: false,
      quantidade: 1,
      viaAdministracao: ADMINISTRATION_WAYS_CENTRAL,
      osmolaridade: null,
      caloriasNaoProteicas: null,
      dataPrescricao: null,
      dataProducao: null,
      dataPrescricaoTime: null,
      dataCriacao: null,
      dataCriacaoTime: null,
      endereco: null,
      registro: null,
      convenio: null,
      leito: null,
      setor: null,
      same: null,
      copy: false,
      model: false,
      profile: false,
      pattern: false,
      transcriptionCopy: false,
      transcricaoEmProgresso: false,
      responsavel: {},
      statusPrescricao: null,
      tipoPrescricao: prescriptionTypes.DEFAULT,
      descricaoTipo: null,
      tipoTranscricao: transcriptionTypes.TRANSCRIPTION,
      terapia: new IdNomeDTO(),
      produtos: [],
      permissao: null,
      enteral: new EnteralPrescricaoDTO(),
      observacao: null,
      observacaoPrescricao: null,
      usoPesoCalorico: false,
      validar: false,
      msgPodeValidar: null,
    };

    this.idPrescricao = initialValue.idPrescricao;
    this.idPrescricaoCopia = initialValue.idPrescricaoCopia;
    this.dataPrescricaoCopia = initialValue.dataPrescricaoCopia;
    this.idTemplate = initialValue.idTemplate;
    this.template = initialValue.template;
    this.idTranscricao = initialValue.idTranscricao;
    this.nomeArquivoTranscricao = initialValue.nomeArquivoTranscricao;
    this.pathArquivoTranscricao = initialValue.pathArquivoTranscricao;
    this.idSiPro = initialValue.idSiPro;
    this.seqsSiProMultiPrescricao = initialValue.seqsSiProMultiPrescricao;
    this.transcriptionCopy = initialValue.transcriptionCopy;
    this.transcricaoEmProgresso = initialValue.transcricaoEmProgresso;
    this.unidade = initialValue.unidade;
    this.cliente = new ClientModel(initialValue.cliente);
    this.medico = new DoctorModel(initialValue.medico);
    this.paciente = new PatientModel(initialValue.paciente);
    this.volumeFinalMlKgDia = initialValue.volumeFinalMlKgDia;
    this.volumeDigitadoPorKg = initialValue.volumeDigitadoPorKg;
    this.divisaoBolsas = initialValue.divisaoBolsas;
    this.bolsaMulticompart = initialValue.bolsaMulticompart;
    this.quantidade = initialValue.quantidade;
    this.viaAdministracao = initialValue.viaAdministracao;
    this.osmolaridade = initialValue.osmolaridade;
    this.caloriasNaoProteicas = initialValue.caloriasNaoProteicas;
    this.dataPrescricao = [];
    if (initialValue.dataPrescricao) {
      this.dataPrescricao.push(
        dateFormat(initialValue.dataPrescricao[0], 'US'),
      );
    } else {
      this.dataPrescricao.push(getServerISODate());
    }
    this.dataProducao = initialValue.dataProducao,
    this.dataPrescricaoTime = initialValue.dataPrescricaoTime;
    this.dataCriacao = !!initialValue.dataCriacao
      ? dateFormat(initialValue.dataCriacao, 'US')
      : null;
    this.dataCriacaoTime = initialValue.dataCriacaoTime;
    this.endereco = initialValue.endereco;
    this.registro = initialValue.registro;
    this.convenio = initialValue.convenio;
    this.leito = initialValue.leito;
    this.setor = initialValue.setor;
    this.same = initialValue.same;
    this.tipoPrescricao = initialValue.tipoPrescricao;
    this.descricaoTipo = initialValue.descricaoTipo;
    this.tipoTranscricao = initialValue.tipoTranscricao;
    this.responsavel = initialValue.responsavel;
    this.copy =
      typeof initialValue.copy !== 'undefined' ? initialValue.copy : false;
    this.model =
      typeof initialValue.model !== 'undefined' ? initialValue.model : false;
    this.profile =
      typeof initialValue.profile !== 'undefined'
        ? initialValue.profile
        : false;
    this.pattern =
      typeof initialValue.pattern !== 'undefined'
        ? initialValue.pattern
        : false;
    this.statusPrescricao = initialValue.statusPrescricao;
    this.produtos = this.formatProducts(initialValue.produtos);
    this.permissao = initialValue.permissao;
    this.bolsaMulticompart = initialValue.bolsaMulticompart;
    this.observacao = initialValue.observacao;
    this.observacaoPrescricao = initialValue.observacaoPrescricao;
    this.usoPesoCalorico = initialValue.usoPesoCalorico;
    this.terapia = initialValue.terapia;
    this.enteral = initialValue.enteral; 
    this.validar = initialValue.validar;
    this.msgPodeValidar = initialValue.msgPodeValidar;   
  }

  // @Override
  public isBaseInfoLocked(): boolean {
    const areLockedValuesUnchanged = {
      copy: !this.copy,
      model: !this.model,
      profile: !this.profile,
      pattern: !this.pattern,
    };

    const isLocked = Object.values(areLockedValuesUnchanged).some(
      (i) => i === false,
    );

    return isLocked;
  }

  // @Override
  public isLocked(): boolean {
    const areLockedValuesUnchanged = {
      divisaoBolsas: this.divisaoBolsas === 1,
      volumeDesejado: !this.volumeDesejado,
      volumeFinalMlKgDia: !this.volumeFinalMlKgDia,
      volumeDigitado: !this.volumeDigitado,
      viaAdministracao: this.viaAdministracao === ADMINISTRATION_WAYS_CENTRAL,
      tempoInfusao: !this.tempoInfusao,
      registro: !this.registro,
      endereco: !this.endereco,
      copy: !this.copy,
      model: !this.model,
      profile: !this.profile,
      pattern: !this.pattern,
      produtos: !this.produtos.length,
    };

    const isLocked = Object.values(areLockedValuesUnchanged).some(
      (i) => i === false,
    );

    return isLocked;
  }

  // @Override
  public isEdit(): boolean {
    return false;
  }

  // @Override
  public isVisualization(): boolean {
    return !!this.idPrescricao;
  }

  // @Override
  public isTranscription(): boolean {
    return (
      this.tipoTranscricao === transcriptionTypes.TRANSCRIPTION ||
      this.tipoTranscricao === transcriptionTypes.TRANSCRIPTION_CHECK ||
      this.tipoTranscricao === transcriptionTypes.TRANSCRIPTION_PHARMACEUTICAL
    );
  }

  // @Override
  public isTranscriptionCopy(): boolean {
    return (
      (this.tipoTranscricao === transcriptionTypes.TRANSCRIPTION ||
        this.tipoTranscricao ===
        transcriptionTypes.TRANSCRIPTION_PHARMACEUTICAL) &&
      !!this.transcriptionCopy
    );
  }

  // @Override
  public isCopy(): boolean {
    return !!this.copy;
  }

  // @Override
  public isModel(): boolean {
    return !!this.model;
  }

  // @Override
  public isProfile(): boolean {
    return !!this.profile;
  }

  // @Override
  public isPattern(): boolean {
    return !!this.pattern;
  }

  // @Override
  public valid(): boolean {
    return (
      !!this.unidade &&
      !!this.unidade.cliente &&
      !!this.unidade.cliente.id &&
      !!this.medico &&
      !!this.medico.id &&
      !!this.paciente &&
      !!this.paciente.id &&
      !!this.paciente.peso &&
      !!this.divisaoBolsas &&
      !!this.viaAdministracao &&
      !!this.produtos.length &&
      !!this.dataPrescricao && 
      !!this.dataPrescricao.length
    );
  }

  public arrayDatasFormart(datas: string[]) {
    const datasFormatadas = [];
    datas.forEach(function(item, indice, array) {
      datasFormatadas.push(dateFormat(item, 'ISO', 'YYYY-MM-DD'));
    });
    return datasFormatadas;
  }

  public getUsedWeightPatient(): Number {
    if (!this.paciente) {
      return null;
    }
    if (this.paciente.imc) {
      return this.paciente.imc.pesoIdeal;
    }
    if (this.usoPesoCalorico && this.paciente.pesoCalorico) {
      return this.paciente.pesoCalorico;
    }
    return this.paciente.peso;
  }

  // @Override
  public toJson(): any {
    const datasFormatas = this.arrayDatasFormart(this.dataPrescricao);

    let template;
    if (!!this.idTemplate) {
      template = {
        id: this.idTemplate,
      };
    }

    if (!this.tipoPrescricao) {
      this.tipoPrescricao = prescriptionTypes.DEFAULT;
    }

    let address = null;
    if (!!this.endereco) {
      address = {
        id: this.endereco.id,
      };
    }

    let transcription;
    if (!!this.idTranscricao) {
      transcription = {
        id: this.idTranscricao,
      };
    }
    let setor = null;
    if (!!this.setor && !!this.setor.id) {
      setor = {
        id: this.setor.id,
      };
    }

    return {
      idPrescricao: this.idPrescricao,
      idPrescricaoCopia: this.idPrescricaoCopia,
      transcricao: transcription,
      template,
      unidade: {
        id: this.unidade.id,
      },
      medico: {
        id: this.medico.id,
        times: this.medico.times,
      },
      paciente: {
        id: this.paciente.id,
        peso: this.paciente.peso,
        termo: this.paciente.termo,
      },
      dataPrescricao: datasFormatas,
      registro: toUpperCase(this.registro),
      setor,
      leito: toUpperCase(this.leito),
      convenio: this.convenio,
      endereco: address,
      tempoInfusao: this.tempoInfusao,
      divisaoBolsas: this.divisaoBolsas,
      bolsaMulticompart: this.bolsaMulticompart,
      quantidade: this.quantidade,
      viaAdministracao: this.viaAdministracao,
      volumeDesejado: this.volumeDesejado || this.volumeDigitado,
      produtos: this.produtos,
      caloriasNaoProteicas: toNumber(this.caloriasNaoProteicas),
      tipoPrescricao: this.tipoPrescricao,
      terapia: this.terapia,
      enteral: this.enteral,
      tipoTranscricao: this.tipoTranscricao,
      observacaoPrescricao: this.observacaoPrescricao,
      usoPesoCalorico: this.usoPesoCalorico,
      codInsumosCienteObrig: this.codInsumosCienteObrig,
    };
  }
}
